// src/components/Calendar.js
import React, { useEffect, useState, useRef, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import { format, addDays, differenceInDays, parseISO, subDays, addMonths, isWeekend, isSameDay, startOfDay, isToday, isPast } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import './Calendar.css';
import ListingRow from '../components/CalendarListingRow';
import ReservationPopup from '../components/ReservationPopup';
import { getCleanersForReservations, getCalendarPricing, supabase } from '../supabase'; // Import supabase client
// import AuthContext from '../AuthContext';

// Constants to avoid recreating objects
const CELL_WIDTH = 60; // Reduced from 70px to 60px
const HEADER_WIDTH = 150;
const ROW_HEIGHT = 50;

// Constants for infinite date scrolling
const INITIAL_PAST_DAYS = 365; // Start with a 1 year of past dates
const INITIAL_FUTURE_DAYS = 365; // Start with 1 year of future dates
const LOAD_THRESHOLD = 30; // Load more dates when we're within this many days of the edge
const DAYS_TO_ADD = 365; // Add a year of dates at a time when loading more

const Calendar = () => {
    const [listings, setListings] = useState([]);
    const [allListings, setAllListings] = useState([]); // Added state for all listings
    const [dates, setDates] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [showReservationPopup, setShowReservationPopup] = useState(false);
    const [cleanerAssignments, setCleanerAssignments] = useState({});
    const [pricingData, setPricingData] = useState({});
    const scrollContainerRef = useRef(null);
    
    // Use startOfDay to ensure consistent date comparison
    const todaysDate = useMemo(() => startOfDay(new Date()), []);
    
    // Add a ref to track if initial scroll has happened
    const initialScrollDoneRef = useRef(false);
    
    // Add state for virtualization - use more efficient approach
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: 10 });
    
    // Throttle scroll handling using refs
    const lastScrollTimeRef = useRef(0);
    const pendingScrollUpdateRef = useRef(false);
    
    // Use ref for visible date range to avoid triggering renders
    const visibleDateRangeRef = useRef({
        start: subDays(todaysDate, 30),
        end: addDays(todaysDate, 30)
    });
    
    // Track loaded date ranges to avoid duplicate fetches
    const loadedDateRangesRef = useRef([]);
    // Also track if we're currently fetching a range to prevent duplicate requests
    const isFetchingRef = useRef(false);
    
    // Track last fetch time to throttle API calls
    const lastFetchTimeRef = useRef(0);

    // Track pricing data loaded ranges separately
    const pricingLoadedRangesRef = useRef([]);

    // Track first and last date in our dates array for infinite scrolling
    const firstDateRef = useRef(null);
    const lastDateRef = useRef(null);
    
    // Initialize dates for infinite scrolling
    useEffect(() => {
        // Define the initial calendar range
        const startDate = subDays(todaysDate, INITIAL_PAST_DAYS);
        const endDate = addDays(todaysDate, INITIAL_FUTURE_DAYS);
        const dateArray = [];
        let currentDate = startDate;
        
        while (currentDate <= endDate) {
            dateArray.push(new Date(currentDate));
            currentDate = addDays(currentDate, 1);
        }
        
        setDates(dateArray);
        
        // Set the first and last date refs
        firstDateRef.current = startDate;
        lastDateRef.current = endDate;
        
        // Set initial visible date range
        visibleDateRangeRef.current = {
            start: subDays(todaysDate, 30),
            end: addDays(todaysDate, 30)
        };
    }, [todaysDate]);

    // Function to add more dates to the beginning of the array
    const loadMorePastDates = useCallback(() => {
        if (!firstDateRef.current || dates.length === 0) return;
        
        console.log("Loading more past dates...");
        
        const newStartDate = subDays(firstDateRef.current, DAYS_TO_ADD);
        const dateArray = [];
        let currentDate = newStartDate;
        
        // Generate new past dates until we reach the current first date
        while (currentDate < firstDateRef.current) {
            dateArray.push(new Date(currentDate));
            currentDate = addDays(currentDate, 1);
        }
        
        // Update first date ref
        firstDateRef.current = newStartDate;
        
        // Add the new dates to the beginning of the array
        setDates(prevDates => [...dateArray, ...prevDates]);
        
        console.log(`Added ${dateArray.length} more past dates. New start date: ${format(newStartDate, 'yyyy-MM-dd')}`);
    }, [dates.length]);

    // Function to add more dates to the end of the array
    const loadMoreFutureDates = useCallback(() => {
        if (!lastDateRef.current || dates.length === 0) return;
        
        console.log("Loading more future dates...");
        
        const newEndDate = addDays(lastDateRef.current, DAYS_TO_ADD);
        const dateArray = [];
        let currentDate = addDays(lastDateRef.current, 1); // Start one day after current last date
        
        // Generate new future dates
        while (currentDate <= newEndDate) {
            dateArray.push(new Date(currentDate));
            currentDate = addDays(currentDate, 1);
        }
        
        // Update last date ref
        lastDateRef.current = newEndDate;
        
        // Add the new dates to the end of the array
        setDates(prevDates => [...prevDates, ...dateArray]);
        
        console.log(`Added ${dateArray.length} more future dates. New end date: ${format(newEndDate, 'yyyy-MM-dd')}`);
    }, [dates.length]);

    // Helper function to consolidate overlapping date ranges
    const consolidateLoadedRanges = useCallback(() => {
        if (loadedDateRangesRef.current.length <= 1) return;
        
        // Sort ranges by start date
        loadedDateRangesRef.current.sort((a, b) => a.start - b.start);
        
        const consolidatedRanges = [];
        let currentRange = loadedDateRangesRef.current[0];
        
        for (let i = 1; i < loadedDateRangesRef.current.length; i++) {
            const nextRange = loadedDateRangesRef.current[i];
            
            // Check if ranges overlap or are adjacent
            if (nextRange.start <= addDays(currentRange.end, 1)) {
                // Merge ranges
                currentRange.end = new Date(Math.max(currentRange.end, nextRange.end));
            } else {
                // No overlap, add current range and start a new one
                consolidatedRanges.push(currentRange);
                currentRange = nextRange;
            }
        }
        
        // Add the last range
        consolidatedRanges.push(currentRange);
        
        // Update the ref
        loadedDateRangesRef.current = consolidatedRanges;
    }, []);

    // Function to check if a date range needs to be loaded
    const shouldLoadDateRange = useCallback((startDate, endDate, isPricingCheck = false) => {
        // Check if this range is already being fetched
        if (isFetchingRef.current) {
            return false;
        }
        
        // Throttle API calls (max one call every 500ms) - increased from 300ms
        const now = Date.now();
        if (now - lastFetchTimeRef.current < 500) {
            return false;
        }
        
        // Get the correct loaded ranges reference based on what we're checking
        const loadedRanges = isPricingCheck ? pricingLoadedRangesRef.current : loadedDateRangesRef.current;
        
        // Check if this range overlaps with already loaded ranges
        const isAlreadyLoaded = loadedRanges.some(range => {
            return (
                (startDate >= range.start && startDate <= range.end) &&
                (endDate >= range.start && endDate <= range.end)
            );
        });
        
        return !isAlreadyLoaded;
    }, []);

    // Separate function to fetch pricing data
    const fetchPricingForDateRange = useCallback(async (listingIds, startDate, endDate) => {
        if (!listingIds || listingIds.length === 0) return;
        
        // Check if we need to load this pricing data
        if (!shouldLoadDateRange(startDate, endDate, true)) {
            console.log('Skipping pricing data fetch - range already loaded');
            return;
        }
        
        // Format dates to strings if they are Date objects
        const startDateStr = typeof startDate === 'string' ? 
            startDate : format(startDate, 'yyyy-MM-dd');
        const endDateStr = typeof endDate === 'string' ? 
            endDate : format(endDate, 'yyyy-MM-dd');
        
        console.log(`Fetching pricing data for ${listingIds.length} listings from ${startDateStr} to ${endDateStr}`);
        
        try {
            // Fetch pricing data
            const newPricing = await getCalendarPricing(listingIds, startDateStr, endDateStr);
            
            // Get a count of prices for logging
            let totalPriceCount = 0;
            Object.values(newPricing).forEach(listingPrices => {
                totalPriceCount += Object.keys(listingPrices).length;
            });
            console.log(`Fetched ${totalPriceCount} total prices for ${Object.keys(newPricing).length} listings`);
            
            // Merge with existing pricing data
            setPricingData(prev => {
                const merged = { ...prev };
                
                // Merge the pricing data
                Object.entries(newPricing).forEach(([listingId, dateMap]) => {
                    if (!merged[listingId]) {
                        merged[listingId] = {};
                    }
                    
                    // Merge the dates for this listing
                    Object.entries(dateMap).forEach(([dateStr, priceInfo]) => {
                        merged[listingId][dateStr] = priceInfo;
                    });
                });
                
                return merged;
            });
            
            // Add this range to the loaded pricing ranges
            pricingLoadedRangesRef.current.push({ start: startDate, end: endDate });
            
            // Consolidate overlapping pricing date ranges
            if (pricingLoadedRangesRef.current.length > 1) {
                // Sort ranges by start date
                pricingLoadedRangesRef.current.sort((a, b) => a.start - b.start);
                
                const consolidatedRanges = [];
                let currentRange = pricingLoadedRangesRef.current[0];
                
                for (let i = 1; i < pricingLoadedRangesRef.current.length; i++) {
                    const nextRange = pricingLoadedRangesRef.current[i];
                    
                    // Check if ranges overlap or are adjacent
                    if (nextRange.start <= addDays(currentRange.end, 1)) {
                        // Merge ranges
                        currentRange.end = new Date(Math.max(currentRange.end, nextRange.end));
                    } else {
                        // No overlap, add current range and start a new one
                        consolidatedRanges.push(currentRange);
                        currentRange = nextRange;
                    }
                }
                
                // Add the last range
                consolidatedRanges.push(currentRange);
                
                // Update the ref
                pricingLoadedRangesRef.current = consolidatedRanges;
            }
            
            // Force a render update with lastUpdated to ensure the UI reflects the new data
            setLastUpdated(Date.now());
            
        } catch (error) {
            console.error('Error fetching pricing data:', error);
        }
    }, [shouldLoadDateRange]);

    // Fetch all listings directly from Supabase
    const fetchAllListings = useCallback(async () => {
        try {
            // Query the listings table directly from Supabase
            const { data, error } = await supabase
                .from('listings')
                .select('id, name, internal_listing_name, external_listing_name, description, address, city, state, country')
                .order('internal_listing_name', { ascending: true });
            
            if (error) throw error;
            
            // Format the data to match the expected structure
            const formattedListings = data.map(listing => ({
                listingId: listing.id,
                listingName: listing.internal_listing_name || listing.external_listing_name || listing.name,
                description: listing.description,
                address: listing.address,
                city: listing.city,
                state: listing.state,
                country: listing.country,
                status: 'active' // Default status
            }));
            
            setAllListings(formattedListings);
            return formattedListings;
        } catch (error) {
            console.error('Error fetching all listings from Supabase:', error.message);
            return [];
        }
    }, []);

    // Function to fetch reservations for a specific date range
    const fetchReservationsForDateRange = useCallback(async (startDate, endDate) => {
        // Format dates for API call
        const start = format(startDate, 'yyyy-MM-dd');
        const end = format(endDate, 'yyyy-MM-dd');
        
        // Check if we need to load this range
        if (!shouldLoadDateRange(startDate, endDate)) {
            return;
        }
        
        // Update last fetch time
        lastFetchTimeRef.current = Date.now();
        
        // Set fetching flag
        isFetchingRef.current = true;
        
        try {
            // Set loading state based on whether we have any listings
            if (listings.length === 0) {
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }
            
            // Make API call with date range parameters
            const response = await axios.get(`https://hostaway.stevenbrekalo.com/api/reservations?dateStart=${start}&dateEnd=${end}`);
            
            // If this is the first load, fetch all listings from Supabase and merge them
            if (listings.length === 0) {
                const allListingsData = await fetchAllListings();
                
                // Create a map of listings with reservations by ID
                const listingsWithReservations = new Map(response.data.map(listing => [listing.listingId, listing]));
                
                // Create the complete listings array including those without reservations
                const completeListings = allListingsData.map(listing => {
                    const listingWithReservations = listingsWithReservations.get(listing.listingId);
                    if (listingWithReservations) {
                        // If this listing has reservations, use that full data
                        return listingWithReservations;
                    } else {
                        // If listing has no reservations, create a structure with empty reservations array
                        return {
                            ...listing,
                            reservations: [] // Empty reservations array
                        };
                    }
                });
                
                setListings(completeListings);
                
                // After setting listings, fetch cleaner assignments and pricing data
                if (completeListings.length > 0) {
                    // Get all reservation IDs from all listings
                    const allReservationIds = completeListings
                        .flatMap(listing => (listing.reservations || []).map(res => res.id))
                        .filter(id => id); // Filter out undefined/null
                        
                    // Fetch cleaner assignments in batch (only if there are reservations)
                    if (allReservationIds.length > 0) {
                        const assignments = await getCleanersForReservations(allReservationIds);
                        setCleanerAssignments(assignments);
                    }
                    
                    // Get listing IDs for pricing data
                    const listingIds = completeListings.map(listing => listing.listingId);
                    
                    // Fetch pricing data
                    await fetchPricingForDateRange(listingIds, startDate, endDate);
                }
            } else {
                // Otherwise, merge the new data with existing data
                setListings(prevListings => {
                    // Create a map of existing listings by ID for efficient lookup
                    const listingMap = new Map(prevListings.map(listing => [listing.listingId, listing]));
                    
                    // Process each new listing with reservations
                    response.data.forEach(newListing => {
                        const existingListing = listingMap.get(newListing.listingId);
                        
                        if (existingListing) {
                            // Listing exists, need to merge reservations
                            const existingReservationIds = new Set(
                                (existingListing.reservations || []).map(r => r.id)
                            );
                            
                            // Filter out duplicates for this listing
                            const newReservations = newListing.reservations.filter(
                                r => !existingReservationIds.has(r.id)
                            );
                            
                            // Add new reservations to existing listing
                            existingListing.reservations = [
                                ...(existingListing.reservations || []),
                                ...newReservations
                            ];
                        } else {
                            // New listing, add to map
                            listingMap.set(newListing.listingId, newListing);
                        }
                    });
                    
                    // Convert map back to array, preserving original order
                    return prevListings.map(listing => 
                        listingMap.get(listing.listingId) || listing
                    );
                });
                
                // Get all reservation IDs from the new listings
                const newReservationIds = response.data
                    .flatMap(listing => listing.reservations.map(res => res.id));
                
                // Fetch only assignments for the new reservations
                const newAssignments = await getCleanersForReservations(newReservationIds);
                
                // Merge with existing assignments
                setCleanerAssignments(prev => ({
                    ...prev,
                    ...newAssignments
                }));
                
                // Get listing IDs for pricing data - use visible listings instead of all listings
                // Get unique listing IDs from both currently visible listings and newly fetched listings
                const currentListingIds = listings.map(listing => listing.listingId);
                const newFetchedListingIds = response.data.map(listing => listing.listingId);
                const listingIds = [...new Set([...currentListingIds, ...newFetchedListingIds])];
                
                // Fetch pricing data for the new range
                await fetchPricingForDateRange(listingIds, startDate, endDate);
            }
            
            // Add this range to the loaded ranges
            loadedDateRangesRef.current.push({ start: startDate, end: endDate });
            
            // Consolidate overlapping date ranges for efficiency
            consolidateLoadedRanges();
        } catch (error) {
            console.error('Error fetching reservations:', error.message);
            setError('Failed to load reservation data.');
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
            isFetchingRef.current = false;
        }
    }, [listings, shouldLoadDateRange, fetchAllListings, consolidateLoadedRanges, fetchPricingForDateRange]);

    // Fetch initial data when component mounts
    useEffect(() => {
        if (dates.length > 0 && !initialScrollDoneRef.current) {
            // Initial data fetch after dates are set
            const { start, end } = visibleDateRangeRef.current;
            if (start && end) {
                fetchReservationsForDateRange(start, end);
            }
        }
    }, [dates, fetchReservationsForDateRange]);

    // Handler for reservation click
    const handleReservationClick = useCallback((reservation, listing) => {
        // Create a data structure that matches what ReservationPopup expects
        const reservationData = {
            recipient_name: reservation.guest_name,
            recipient_picture: reservation.guest_picture || 'https://via.placeholder.com/50',
            reservations: reservation,
            listings: {
                internal_listing_name: listing.listingName
            }
        };
        
        setSelectedReservation(reservationData);
        setShowReservationPopup(true);
    }, []);

    // Calculate visible listings based on scroll position - optimized
    const calculateVisibleListings = useCallback(() => {
        if (!scrollContainerRef.current || !listings.length) return;
        
        const container = scrollContainerRef.current;
        const containerHeight = container.clientHeight;
        const scrollTop = container.scrollTop;
        
        // Calculate which rows should be visible
        // Using Math.floor to avoid decimal issues
        const startIndex = Math.floor(scrollTop / ROW_HEIGHT);
        // Buffer with 3 rows above and below for smoother scrolling
        const visibleRowsCount = Math.ceil(containerHeight / ROW_HEIGHT) + 6;
        const endIndex = Math.min(listings.length, startIndex + visibleRowsCount);
        
        // Only update state if actually changed
        if (startIndex !== visibleRange.start || endIndex !== visibleRange.end) {
            setVisibleRange({
                start: Math.max(0, startIndex),
                end: endIndex
            });
        }
    }, [listings.length, visibleRange.start, visibleRange.end]);

    // Function to update the visible date range based on scroll position - optimized with infinite loading
    const updateVisibleDateRange = useCallback((scrollLeft) => {
        if (!dates.length || !scrollContainerRef.current) return;
        
        const containerWidth = scrollContainerRef.current.clientWidth;
        const visibleDaysCount = Math.ceil(containerWidth / CELL_WIDTH);
        
        // Calculate the visible day indices
        const startDayIndex = Math.max(0, Math.floor(scrollLeft / CELL_WIDTH));
        const endDayIndex = Math.min(dates.length - 1, startDayIndex + visibleDaysCount + 10); // Increased buffer
        
        // Check if we should load more past dates
        if (startDayIndex < LOAD_THRESHOLD && dates.length > 0) {
            loadMorePastDates();
        }
        
        // Check if we should load more future dates
        if (dates.length > 0 && endDayIndex > dates.length - LOAD_THRESHOLD) {
            loadMoreFutureDates();
        }
        
        // Get the actual dates
        const startDate = dates[startDayIndex];
        const endDate = dates[endDayIndex];
        
        // Add buffer for preloading (30 days before and after instead of 20)
        const bufferStartDate = subDays(startDate, 30);
        const bufferEndDate = addDays(endDate, 30);
        
        // Store previous date range for comparison
        const prevStart = visibleDateRangeRef.current.start;
        const prevEnd = visibleDateRangeRef.current.end;
        
        // Always update the ref to ensure it has the latest values
        visibleDateRangeRef.current = { 
            start: bufferStartDate, 
            end: bufferEndDate 
        };
        
        // Check if the date range has changed significantly
        const hasChangedSignificantly = 
            !prevStart || 
            !prevEnd ||
            differenceInDays(bufferStartDate, prevStart) < -2 || 
            differenceInDays(bufferStartDate, prevStart) > 2 ||
            differenceInDays(bufferEndDate, prevEnd) < -2 || 
            differenceInDays(bufferEndDate, prevEnd) > 2;
        
        // Fetch data for the new range if changed significantly
        if (hasChangedSignificantly) {
            console.log(`Date range changed significantly - fetching new data`);
            fetchReservationsForDateRange(bufferStartDate, bufferEndDate);
            
            // Always fetch pricing data for all listings to ensure coverage
            if (listings && listings.length > 0) {
                // Always fetch for all listings since it's more important to have complete data
                const allListingIds = listings.map(listing => listing.listingId);
                console.log(`Fetching pricing data for all ${allListingIds.length} listings on date range change`);
                fetchPricingForDateRange(allListingIds, bufferStartDate, bufferEndDate);
            }

            // Log for debugging purposes
            console.log("Fetching new date range:", format(bufferStartDate, 'yyyy-MM-dd'), "to", format(bufferEndDate, 'yyyy-MM-dd'));
        } else {
            // Even if the range hasn't changed significantly, still fetch pricing data for visible listings
            if (listings && listings.length > 0) {
                const visibleListingIds = listings
                    .slice(visibleRange.start, visibleRange.end)
                    .map(listing => listing.listingId);
                
                if (visibleListingIds.length > 0) {
                    console.log(`Fetching pricing data for ${visibleListingIds.length} visible listings on minor scroll`);
                    fetchPricingForDateRange(visibleListingIds, bufferStartDate, bufferEndDate);
                }
            }
        }
    }, [dates, fetchReservationsForDateRange, listings, fetchPricingForDateRange, visibleRange.start, visibleRange.end, loadMorePastDates, loadMoreFutureDates]);

    // Navigation functions
    const goToToday = useCallback(() => {
        // Set current month to March 2025
        setCurrentMonth(new Date(2025, 2, 1)); // JS months are 0-indexed, so 2 = March
        
        // Find March 1, 2025 in calendar
        const todayStr = "2025-03-01";
        
        let todayIndex = -1;
        for (let i = 0; i < dates.length; i++) {
            const date = dates[i];
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            
            if (dateStr === todayStr) {
                todayIndex = i;
                break;
            }
        }
        
        if (todayIndex !== -1 && scrollContainerRef.current) {
            // Calculate position consistently
            const scrollPosition = (todayIndex * CELL_WIDTH) - 150; // Centering adjustment
            
            // Ensure we don't scroll to a negative position
            scrollContainerRef.current.scrollLeft = Math.max(0, scrollPosition);
            
            // Update visible date range
            updateVisibleDateRange(scrollPosition);
        }
    }, [dates, updateVisibleDateRange]);

    const goToPreviousMonth = useCallback(() => {
        setCurrentMonth(prevMonth => {
            const newMonth = addMonths(prevMonth, -1);
            
            // Find the first day of the new month in our dates array
            const firstDayIndex = dates.findIndex(date => 
                date.getMonth() === newMonth.getMonth() && 
                date.getFullYear() === newMonth.getFullYear() &&
                date.getDate() === 1
            );
            
            // If the month is found in our date range
            if (firstDayIndex !== -1 && scrollContainerRef.current) {
                const scrollPosition = firstDayIndex * CELL_WIDTH;
                scrollContainerRef.current.scrollLeft = scrollPosition;
                
                // Update visible date range
                updateVisibleDateRange(scrollPosition);
            }
            
            return newMonth;
        });
    }, [dates, updateVisibleDateRange]);

    const goToNextMonth = useCallback(() => {
        setCurrentMonth(prevMonth => {
            const newMonth = addMonths(prevMonth, 1);
            
            // Find the first day of the new month in our dates array
            const firstDayIndex = dates.findIndex(date => 
                date.getMonth() === newMonth.getMonth() && 
                date.getFullYear() === newMonth.getFullYear() &&
                date.getDate() === 1
            );
            
            // If the month is found in our date range
            if (firstDayIndex !== -1 && scrollContainerRef.current) {
                const scrollPosition = firstDayIndex * CELL_WIDTH;
                scrollContainerRef.current.scrollLeft = scrollPosition;
                
                // Update visible date range
                updateVisibleDateRange(scrollPosition);
            }
            
            return newMonth;
        });
    }, [dates, updateVisibleDateRange]);

    // Scroll to today ONLY on initial load, not on every render
    useEffect(() => {
        if (dates.length > 0 && !initialScrollDoneRef.current) {
            goToToday();
            initialScrollDoneRef.current = true;
            
            // Initial calculation of visible listings
            setTimeout(calculateVisibleListings, 100);
        }
    }, [dates, goToToday, calculateVisibleListings]);

    // Optimized scroll handler with throttling
    const handleScroll = useCallback(() => {
        // Skip if we're already processing a scroll update
        if (pendingScrollUpdateRef.current) return;
        
        // Throttle scroll updates
        const now = Date.now();
        // Only update every 100ms
        if (now - lastScrollTimeRef.current < 100) {
            // If we need to schedule an update, do it only once
            if (!pendingScrollUpdateRef.current) {
                pendingScrollUpdateRef.current = true;
                setTimeout(() => {
                    // Calculate vertical scroll (listings)
                    calculateVisibleListings();
                    
                    // Calculate horizontal scroll (dates)
                    const scrollLeft = scrollContainerRef.current?.scrollLeft || 0;
                    updateVisibleDateRange(scrollLeft);
                    
                    // Update current month (only occasionally)
                    if (scrollContainerRef.current && dates.length > 0 && Math.random() < 0.1) {
                        const scrollLeft = scrollContainerRef.current.scrollLeft;
                        const visibleDayIndex = Math.floor(scrollLeft / CELL_WIDTH) + 3;
                        
                        if (visibleDayIndex >= 0 && visibleDayIndex < dates.length) {
                            const visibleDate = dates[visibleDayIndex];
                            setCurrentMonth(visibleDate);
                        }
                    }
                    
                    // Also trigger pricing data loading for visible listings
                    if (visibleDateRangeRef.current.start && visibleDateRangeRef.current.end && listings.length > 0) {
                        // Select either all visible listings or all listings based on count
                        const listingIdsToFetch = listings.length < 20 ? 
                            listings.map(listing => listing.listingId) : 
                            listings.slice(visibleRange.start, visibleRange.end).map(listing => listing.listingId);
                        
                        if (listingIdsToFetch.length > 0) {
                            console.log(`Triggering pricing data fetch from scroll handler for ${listingIdsToFetch.length} listings`);
                            fetchPricingForDateRange(
                                listingIdsToFetch, 
                                visibleDateRangeRef.current.start,
                                visibleDateRangeRef.current.end
                            );
                        }
                    }
                    
                    // Reset pending flag and update last scroll time
                    pendingScrollUpdateRef.current = false;
                    lastScrollTimeRef.current = Date.now();
                }, 100);
            }
            return;
        }
        
        // Fast path - update immediately
        calculateVisibleListings();
        
        // Calculate horizontal scroll (dates)
        const scrollLeft = scrollContainerRef.current?.scrollLeft || 0;
        updateVisibleDateRange(scrollLeft);
        
        // Update last scroll time
        lastScrollTimeRef.current = now;
    }, [dates, calculateVisibleListings, updateVisibleDateRange, listings, fetchPricingForDateRange, visibleRange.start, visibleRange.end]);

    // Add scroll event listener and initial calculation
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
            
            return () => {
                scrollContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [handleScroll]);

    // Memoize the date cells rendering to avoid recreating them on each render
    const dateCells = useMemo(() => {
        return dates.map((date) => {
            // Hardcoded March 1, 2025 for today
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const isToday = dateStr === todayStr;
            const isPast = date < new Date(2025, 2, 1); // March 1, 2025
            const isWeekendDay = isWeekend(date);
            
            return (
                <div
                    key={dateStr}
                    className={`calendar-cell ${isPast ? 'past' : ''} ${isToday ? 'today' : ''} ${isWeekendDay ? 'weekend' : ''}`}
                    data-date={dateStr}
                >
                    <div>{format(date, 'EEE')}</div>
                    <div>{format(date, 'd')}</div>
                </div>
            );
        });
    }, [dates]);

    // Generate spacer heights for virtualization
    const topSpacerHeight = visibleRange.start * ROW_HEIGHT;
    const bottomSpacerHeight = (listings.length - visibleRange.end) * ROW_HEIGHT;

    // Get only the visible listings - show ALL listings
    const visibleListings = useMemo(() => {
        return listings.slice(visibleRange.start, visibleRange.end);
    }, [listings, visibleRange.start, visibleRange.end]);

    // Add an effect to fetch pricing data when visible listings change
    useEffect(() => {
        // Only proceed if we have visible listings and dates
        if (!visibleListings.length || !dates.length || !visibleDateRangeRef.current) return;
        
        // Get the current visible date range
        const currentStart = visibleDateRangeRef.current.start;
        const currentEnd = visibleDateRangeRef.current.end;
        
        // Don't proceed if we don't have valid dates
        if (!currentStart || !currentEnd) return;
        
        // Get listing IDs for visible listings
        const visibleListingIds = visibleListings.map(listing => listing.listingId);
        
        // Fetch pricing data for visible listings in the current date range
        if (visibleListingIds.length > 0) {
            console.log("Fetching pricing data for newly visible listings:", visibleListingIds.length);
            fetchPricingForDateRange(visibleListingIds, currentStart, currentEnd);
        }
    }, [visibleListings, fetchPricingForDateRange, dates.length]);

    // Add debug logging for pricing data
    useEffect(() => {
        // Log the current state of pricing data whenever it changes
        const listingIds = Object.keys(pricingData);
        if (listingIds.length > 0) {
            console.log(`Pricing data available for ${listingIds.length} listings`);
            
            // Check if visible listings have pricing data
            if (visibleListings.length > 0) {
                const visibleListingIds = visibleListings.map(listing => listing.listingId);
                const listingsWithPricingData = visibleListingIds.filter(id => pricingData[id]);
                
                console.log(`${listingsWithPricingData.length} of ${visibleListingIds.length} visible listings have pricing data`);
                
                // For the first listing, log the number of dates with pricing
                if (listingsWithPricingData.length > 0) {
                    const firstListingId = listingsWithPricingData[0];
                    const datesWithPricing = Object.keys(pricingData[firstListingId] || {}).length;
                    console.log(`First listing has pricing data for ${datesWithPricing} dates`);
                }
            }
        }
    }, [pricingData, visibleListings]);

    // Extract all reservations from visible listings to get their IDs
    const visibleReservationIds = useMemo(() => {
        if (!visibleListings || visibleListings.length === 0) return [];
        
        // Collect all reservation IDs from visible listings
        const ids = [];
        visibleListings.forEach(listing => {
            if (listing.reservations && listing.reservations.length > 0) {
                // Filter for reservations in the visible date range
                const visibleReservs = listing.reservations.filter(reservation => {
                    if (!visibleDateRangeRef.current) return true;
                    
                    const arrival = parseISO(reservation.arrival_date);
                    const departure = parseISO(reservation.departure_date);
                    
                    return arrival <= visibleDateRangeRef.current.end && 
                           departure >= visibleDateRangeRef.current.start;
                });
                
                visibleReservs.forEach(reservation => {
                    ids.push(reservation.id);
                });
            }
        });
        
        return ids;
    }, [visibleListings]);

    // Fetch cleaner assignments for all visible reservations at once
    useEffect(() => {
        if (visibleReservationIds.length === 0) return;
        
        // Check which reservation IDs we don't already have
        const missingReservationIds = visibleReservationIds.filter(
            id => !cleanerAssignments[id]
        );
        
        if (missingReservationIds.length === 0) return;
        
        // Fetch all missing assignments in one batch
        const fetchCleanerAssignments = async () => {
            try {
                const assignments = await getCleanersForReservations(missingReservationIds);
                
                if (Object.keys(assignments).length > 0) {
                    // Merge with existing assignments
                    setCleanerAssignments(prev => ({
                        ...prev,
                        ...assignments
                    }));
                }
            } catch (error) {
                console.error('Error fetching cleaner assignments:', error);
            }
        };
        
        fetchCleanerAssignments();
    }, [visibleReservationIds, cleanerAssignments]);

    // Add a handler for cleaner assignments
    const handleCleanerAssignment = useCallback((reservationId, cleaner) => {
        console.log('Cleaner assigned in Calendar:', reservationId, cleaner);
        
        // Immediately update the cleanerAssignments state with the new assignment
        setCleanerAssignments(prev => {
            const updated = {
                ...prev,
                [reservationId]: cleaner
            };
            
            // If cleaner is null, remove from assignments
            if (cleaner === null) {
                delete updated[reservationId];
            }
            
            return updated;
        });
        
        // Force a re-render by updating a timestamp
        setLastUpdated(Date.now());
    }, []);

    // Add a state to force re-renders when cleaner assignments change
    const [lastUpdated, setLastUpdated] = useState(Date.now());

    return (
        <div className="calendar-container">
            {/* Calendar Controls - simplified */}
            <div className="calendar-controls">
                <div className="calendar-title">
                    {format(currentMonth, 'MMMM yyyy')}
                </div>
                <div className="calendar-nav-buttons">
                    <button 
                        className="calendar-nav-button" 
                        onClick={goToPreviousMonth}
                    >
                        Previous
                    </button>
                    <button 
                        className="calendar-nav-button today" 
                        onClick={goToToday}
                    >
                        Today
                    </button>
                    <button 
                        className="calendar-nav-button" 
                        onClick={goToNextMonth}
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* Scrollable container that holds both header and body */}
            <div 
                className="calendar-scrollable-container"
                ref={scrollContainerRef}
            >
                <div className="calendar-sticky-header">
                    <div className="calendar-header-placeholder"></div>
                    <div className="calendar-header">
                        {/* Use memoized date cells */}
                        {dateCells}
                    </div>
                </div>

                {/* Calendar Content with Virtualization */}
                <div className="calendar-content">
                    {isLoading ? (
                        <div className="calendar-loading">Loading reservations...</div>
                    ) : listings?.length === 0 ? (
                        <div className="calendar-empty">No listings found</div>
                    ) : (
                        <>
                            {/* Top spacer for virtualization */}
                            {topSpacerHeight > 0 && (
                                <div style={{ height: `${topSpacerHeight}px` }} />
                            )}
                            
                            {/* Only render visible listings */}
                            {visibleListings.map((listing) => (
                                <ListingRow
                                    key={`${listing.listingId}_${lastUpdated}`}
                                    listing={listing}
                                    dates={dates}
                                    visibleDateRange={visibleDateRangeRef.current}
                                    onReservationClick={(reservation) => handleReservationClick(reservation, listing)}
                                    cleanerAssignments={cleanerAssignments}
                                    pricingData={pricingData[listing.listingId] || {}}
                                />
                            ))}
                            
                            {/* Bottom spacer for virtualization */}
                            {bottomSpacerHeight > 0 && (
                                <div style={{ height: `${bottomSpacerHeight}px` }} />
                            )}
                            
                            {/* Loading indicator for additional data */}
                            {isLoadingMore && (
                                <div className="calendar-loading-more">
                                    Loading more data...
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Render the ReservationPopup when a reservation is selected */}
            {showReservationPopup && selectedReservation && (
                <ReservationPopup 
                    reservationData={selectedReservation}
                    showEntireReservation={showReservationPopup}
                    setShowEntireReservation={setShowReservationPopup}
                    cleanerAssignments={cleanerAssignments}
                    onCleanerAssigned={handleCleanerAssignment}
                />
            )}
        </div>
    );
};

export default Calendar;
